import React, { useCallback, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Divider } from '@/app/(main)/clients/create/components/Divider';
import { PersonalInfoSection } from '@/app/(main)/clients/create/components/PersonalInfoSection';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import type { ClientDocumentResponse, CreateClientFormData, CreateRelatedPassengerResponse, EditClientResponse, PassportDataWithName, RelatedPassengerFormData } from '@/types/clientsApiTypes';
import { RELATED_PASSENGER_EMPTY_VALUES } from '../../const/passenger';
import { useGetClientDocuments } from '../../hooks/useGetClientDocuments';
import type { RelatedDataKeys } from '../../hooks/useUpdateTravelPreferencesWithRelatedData/types';
import { getMergedDocuments } from '../../utils/getMergedClientDocuments';
import { getNewPassengerData } from '../../utils/getNewPassengerData';
import { getPassengerTitle } from '../../utils/getPassengerTitle';
import { ComfortDetails } from '../CreateClientComformDetails/index';
import { DocumentsSection } from '../DocumentsSection';
import { LoyaltyProgram } from '../PassengerLoyaltyProgram';
import { RelatedPassengerMailingAddress } from '../RelatedPassengerMailingAddress';
import { RelatedPassengerMainInfo } from '../RelatedPassengerMainInfo';
import { RelatedPassengerTravelPreferencesAllergies } from '../RelatedPassengerTravelPreferencesAllergies';
import { RelatedPassengerTravelPreferencesTravelData } from '../RelatedPassengerTravelPreferencesTravelData';
import { RemovePassengerModal } from '../RemoveRelatedPassengerModal';
import { Section } from '../Section';
import { SectionAccordion } from '../SectionAccordion';
import PlusIcon from '/public/icons/plus.svg';
import TrashIcon from '/public/icons/trash.svg';

interface RelatedPassengerItemProps {
  field: RelatedPassengerFormData;
  index: number;
  onRemove: (index: number) => void;
  isPublicPage?: boolean;
  onSuccessUploadDocuments?: (data: ClientDocumentResponse, index: number) => void;
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => Promise<EditClientResponse | undefined>;
  onUpdateTravelPreferencesWithRelatedData?: (
    fieldKey: RelatedDataKeys,
    fieldName: string,
    fieldValue: unknown,
    fullPath: string
  ) => Promise<EditClientResponse | undefined>;
  onUpdateDocuments?: (documents: PassportDataWithName, documentId: number | null) => void;
  onUpdateTravelPreferences?: (fieldName: string, fieldValue: unknown) => void;
  onDeleteDocuments?: (documentId: number) => void;
  onDeleteLoyaltyProgram?: (lpId: number) => void;
}

const RelatedPassengerItem = React.memo(
  ({
    field,
    index,
    isPublicPage,
    onRemove,
    onSuccessUploadDocuments,
    onUpdateDocuments,
    onDeleteDocuments,
    onUpdateTravelPreferences,
    onUpdateTravelPreferencesWithRelatedData,
    onUpdateFormData,
    onDeleteLoyaltyProgram,
  }: RelatedPassengerItemProps) => {
    const { control, watch } = useFormContext<CreateClientFormData>();
    const { data: documentsData } = useGetClientDocuments(String(field.client.client_id));

    const basePath = `related_passengers.${index}` as const;
    const mappedDocuments = getMergedDocuments(watch(`${basePath}.documents`) || [], documentsData?.data || []);

    const firstName = useWatch({
      control,
      name: `${basePath}.client.first_name`,
    });
    const lastName = useWatch({
      control,
      name: `${basePath}.client.last_name`,
    });

    const handleSuccessUploadDocuments = useCallback(
      (data: ClientDocumentResponse) => {
        if (onSuccessUploadDocuments) {
          onSuccessUploadDocuments(data, index);
        }
      },
      [onSuccessUploadDocuments, index]
    );

    const displayName = getPassengerTitle(firstName, lastName);

    return (
      <SectionAccordion key={field.client.client_id} variant="default" header={displayName}>
        <RelatedPassengerMainInfo basePath={basePath} onUpdateFormData={onUpdateFormData} />
        <Divider className="mx-0" />

        <Section
          className="px-0 py-0"
          title="Mailing Address"
          isLast
          description="Add the passenger’s address for records and correspondence"
        >
          <RelatedPassengerMailingAddress onUpdateFormData={onUpdateFormData} basePath={basePath} />
        </Section>
        <Divider className="mx-0" />
        <div className="mb-6 flex flex-col w-full items-center justify-between">
          <DocumentsSection
            passengerFullName={displayName}
            onSuccess={handleSuccessUploadDocuments}
            onUpdateDocuments={onUpdateDocuments}
            onDeleteDocuments={onDeleteDocuments}
            fieldPrefix={basePath}
            documents={mappedDocuments}
            isPublicPage={isPublicPage}
            passengerId={field.client.client_id}
            tableRowClassName="bg-white"
          />
        </div>
        <Divider className="mx-0" />
        <SectionAccordion variant="white" arrowPosition="left" header="Travel Profile & Preferences">
          <Section className="px-0 pt-0 pb-[30px]" title="Travel Data">
            <RelatedPassengerTravelPreferencesTravelData basePath={basePath} onUpdateFormData={onUpdateFormData} />
          </Section>
          <Section
            description="Applied across flights, hotels, and experiences when relevant."
            className="px-0 pb-0 pt-[30px]"
            title="Loyalty Program"
            isLast
          >
            <LoyaltyProgram
              fieldPrefix={basePath}
              onUpdateFormData={onUpdateFormData}
              onDeleteLoyaltyProgram={onDeleteLoyaltyProgram}
            />
          </Section>
          <Divider className="mx-0" />
          <Section className="px-0 py-0" title="Preferences & Allergies" isLast>
            <RelatedPassengerTravelPreferencesAllergies
              onUpdateFormData={onUpdateTravelPreferences}
              basePath={basePath}
            />
          </Section>
          <Divider className="mx-0" />
          <Section className="px-0 py-0" title="" isLast>
            <ComfortDetails
              fieldPrefix={`${basePath}.preference_data`}
              onUpdateTravelPreferencesWithRelatedData={onUpdateTravelPreferencesWithRelatedData}
            />
            <PersonalInfoSection
              basePath={`${basePath}.preference_data`}
              onUpdateTravelPreferencesWithRelatedData={onUpdateTravelPreferencesWithRelatedData}
            />
          </Section>
        </SectionAccordion>
        <div className="mt-6 flex justify-end">
          <BaseButton
            type="button"
            startIcon={<TrashIcon className="w-4 h-4" />}
            intent="ghost_danger"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove(index);
            }}
          >
            Delete Passenger
          </BaseButton>
        </div>
      </SectionAccordion>
    );
  }
);

RelatedPassengerItem.displayName = 'RelatedPassengerItem';

interface RelatedPassengersProps {
  onDeletePassenger?: (passengerId: number) => void;
  onAddPassenger?: (primaryPassengerId: number) => Promise<CreateRelatedPassengerResponse>;
  onUpdateRelatedPassenger?: (relatedPassengerId: number) => void;
  onDeleteRelatedPassenger?: (relatedPassengerId: number) => void;
  onSuccessUploadDocuments?: (data: ClientDocumentResponse, index: number) => void;
  onUpdateDocuments?: (documents: PassportDataWithName, documentId: number | null) => void;
  onDeleteDocuments?: (documentId: number) => void;
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => Promise<EditClientResponse | undefined>;
  onUpdateTravelPreferences?: (fieldName: string, fieldValue: unknown) => void;
  onUpdateTravelPreferencesWithRelatedData?: (
    fieldKey: RelatedDataKeys,
    fieldName: string,
    fieldValue: unknown,
    fullPath: string
  ) => Promise<EditClientResponse | undefined>;
  onDeleteLoyaltyProgram?: (lpId: number, passengerId: number) => void;
  isPrimaryPassengerValid: boolean;
  primaryPassengerId: number;
  isPublicPage?: boolean;
}
export const RelatedPassengers = ({
  onUpdateFormData,
  onUpdateTravelPreferences,
  onUpdateTravelPreferencesWithRelatedData,
  onAddPassenger,
  onDeletePassenger,
  onSuccessUploadDocuments,
  onDeleteLoyaltyProgram,
  onUpdateDocuments,
  onDeleteDocuments,
  primaryPassengerId,
  isPrimaryPassengerValid,
  isPublicPage,
}: RelatedPassengersProps) => {
  const { control, getValues } = useFormContext<CreateClientFormData>();

  const [isOpenDeleteRelatedModal, setIsOpenDeleteRelatedModal] = useState<{
    isOpen: boolean;
    index: number;
  }>({
    isOpen: false,
    index: 0,
  });

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'related_passengers',
  });

  const handleAddPassenger = useCallback(async () => {
    if (onAddPassenger) {
      const newPassenger = await onAddPassenger(primaryPassengerId);

      if (newPassenger?.data) {
        append(getNewPassengerData(newPassenger.data));
      }

      return;
    }
    append(RELATED_PASSENGER_EMPTY_VALUES);
  }, [onAddPassenger, primaryPassengerId, append, getValues]);

  const handleCloseRemoveModal = useCallback(() => {
    setIsOpenDeleteRelatedModal((prevState) => ({ ...prevState, isOpen: false, index: 0 }));
  }, [setIsOpenDeleteRelatedModal]);

  const handleRemoveRelatedPassenger = useCallback((index: number) => {
    setIsOpenDeleteRelatedModal((prevState) => ({ ...prevState, isOpen: true, index }));
  }, [setIsOpenDeleteRelatedModal]);
  
  const handleConfirmRemove = useCallback(
    (index: number) => {
      if (onDeletePassenger && fields[index]?.client?.client_id) {
        onDeletePassenger(fields[index].client.client_id);
      }
      remove(isOpenDeleteRelatedModal.index);
      handleCloseRemoveModal();
    },
    [onDeletePassenger, primaryPassengerId, fields, remove, isOpenDeleteRelatedModal.index, handleCloseRemoveModal]
  );

  const handleDeleteLoyaltyProgram = useCallback(
    (passengerId: number, lpId: number) => {
      if (onDeleteLoyaltyProgram) {
        return onDeleteLoyaltyProgram(passengerId, lpId);
      }
    },
    [onDeleteLoyaltyProgram]
  );

  return (
    <div className="flex flex-col gap-6 mt-3">
      <>
        {fields.map((field, index) => (
          <RelatedPassengerItem
            key={field.client.client_id}
            field={field}
            index={index}
            onRemove={() => handleRemoveRelatedPassenger(index)}
            onUpdateTravelPreferences={onUpdateTravelPreferences}
            onUpdateTravelPreferencesWithRelatedData={onUpdateTravelPreferencesWithRelatedData}
            onSuccessUploadDocuments={onSuccessUploadDocuments}
            onUpdateFormData={onUpdateFormData}
            onUpdateDocuments={onUpdateDocuments}
            onDeleteDocuments={onDeleteDocuments}
            onDeleteLoyaltyProgram={(lpId) => handleDeleteLoyaltyProgram(field.client.client_id, lpId)}
            isPublicPage={isPublicPage}
          />
        ))}
      </>
      <RemovePassengerModal
        onClose={handleCloseRemoveModal}
        isPending={false}
        isOpen={isOpenDeleteRelatedModal.isOpen}
        onConfirm={() => handleConfirmRemove(isOpenDeleteRelatedModal.index)}
      />

      <div className="mt-4">
        <BaseButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleAddPassenger();
          }}
          startIcon={<PlusIcon className="w-4 h-4" />}
          type="button"
          disabled={!isPrimaryPassengerValid && fields.length === 0}
          intent="secondary"
        >
          Add Passenger
        </BaseButton>
      </div>
    </div>
  );
};